/**
 * Product hero (shop mode) — variant matching, computed per-unit pricing,
 * gallery sync, click-to-zoom, sticky-on-scroll bar, qty stepper, and a
 * fetch('/cart/add.js') add-to-cart that dispatches Shopify's real
 * CartLinesUpdateEvent (same contract Horizon's own <product-form> uses).
 * cart-items-component and cart-icon.js already listen for this event
 * natively and morph/update themselves — letting them do that, instead of
 * hand-rolling a DOMParser+replaceWith swap (the previous approach, which
 * only unreliably refreshed the drawer), is what actually gets the open
 * drawer and header bubble to reflect the new item correctly every time.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  'use strict';

  var PACK_QTY_RE = /(\d+(?:\.\d+)?)\s*pcs/i;

  // Shopify's image_tag filter auto-generates a `srcset` on the main/sticky
  // <img> tags. Per the HTML spec, when `srcset` is present the browser
  // resolves the DISPLAYED image from srcset (ignoring plain `src` changes
  // made via JS) — so setting `.src` alone silently did nothing visually.
  // Clearing `srcset`/`sizes` before setting `.src` is required every time.
  // (Same root cause and fix as assets/product-main-catalog.js.)
  function setImageSrc(imgEl, src) {
    if (!imgEl || !src) return;
    imgEl.removeAttribute('srcset');
    imgEl.removeAttribute('sizes');
    imgEl.src = src;
  }

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withSeparators = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var formatted = withSeparators + '.' + parts[1];
    if (format && format.indexOf('{{amount}}') > -1) {
      return format.replace('{{amount}}', formatted);
    }
    return '€' + formatted;
  }

  function parsePackQty(value) {
    var match = PACK_QTY_RE.exec(value || '');
    return match ? parseFloat(match[1]) : null;
  }

  function initProductMain(root) {
    var dataScript = root.querySelector('[data-role="pms-variant-data"]');
    if (!dataScript) return;

    var data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    var variants = data.variants || [];
    var media = data.media || [];
    var groups = root.querySelectorAll('[data-role="variant-group"]');
    var selected = {};
    var qty = 1;

    groups.forEach(function (group) {
      var activeBtn = group.querySelector('.is-active') || group.querySelector('button');
      var optionIndex = group.getAttribute('data-option-index');
      selected[optionIndex] = activeBtn ? activeBtn.getAttribute('data-value') : null;
    });

    function findVariant(overrides) {
      var combo = Object.assign({}, selected, overrides || {});
      var optionIndexes = Object.keys(combo).sort();
      return variants.filter(function (variant) {
        return optionIndexes.every(function (idx) {
          return variant.options[Number(idx)] === combo[idx];
        });
      })[0];
    }

    function unitPrice(variant) {
      if (!variant) return null;
      for (var i = 0; i < variant.options.length; i++) {
        var qtyInValue = parsePackQty(variant.options[i]);
        if (qtyInValue) return variant.price / qtyInValue;
      }
      return null;
    }

    function updateGalleryForVariant(variant) {
      if (!variant) return;
      var mainMedia = root.querySelector('[data-role="main-media"] img');
      var stickyImage = root.querySelector('[data-role="sticky-image"] img');
      if (variant.featured_image) {
        setImageSrc(mainMedia, variant.featured_image);
        setImageSrc(stickyImage, variant.featured_image);
        if (variant.featured_media_id) {
          root.querySelectorAll('[data-role="thumb"]').forEach(function (thumb) {
            thumb.classList.toggle('is-active', thumb.getAttribute('data-media-id') === String(variant.featured_media_id));
          });
        }
      }
    }

    function applyVariant(variant) {
      if (!variant) return;

      var priceEl = root.querySelector('[data-role="price"]');
      var comparePriceEl = root.querySelector('[data-role="compare-price"]');
      var pricePerEl = root.querySelector('[data-role="price-per"]');
      var skuEl = root.querySelector('[data-role="sku"]');
      var variantIdInput = root.querySelector('[data-role="variant-id-input"]');
      var addToCartBtn = root.querySelector('[data-role="add-to-cart"]');
      var stickyAddToCartBtn = root.querySelector('[data-role="sticky-add-to-cart"]');
      var stockRow = root.querySelector('[data-role="stock-row"]');
      var stockText = root.querySelector('[data-role="stock-text"]');
      var stickyVariant = root.querySelector('[data-role="sticky-variant"]');
      var stickyPrice = root.querySelector('[data-role="sticky-price"]');
      var orderTotal = root.querySelector('[data-role="order-total"]');

      if (priceEl) priceEl.textContent = formatMoney(variant.price, data.moneyFormat);

      if (comparePriceEl) {
        if (variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price, data.moneyFormat);
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.hidden = true;
        }
      }

      var perUnit = unitPrice(variant);
      if (pricePerEl) {
        if (perUnit) {
          pricePerEl.textContent = formatMoney(Math.round(perUnit), data.moneyFormat) + ' / piece';
          pricePerEl.hidden = false;
        } else {
          pricePerEl.hidden = true;
        }
      }

      if (skuEl) skuEl.textContent = 'SKU: ' + (variant.sku || '');
      if (variantIdInput) variantIdInput.value = variant.id;

      [addToCartBtn, stickyAddToCartBtn].forEach(function (btn) {
        if (btn) btn.disabled = !variant.available;
      });

      if (stockRow && stockText) {
        stockRow.toggleAttribute('data-sold-out', !variant.available);
        if (!variant.available) {
          stockText.textContent = 'Sold out';
        } else if (variant.inventory_management && variant.inventory_quantity <= 10 && variant.inventory_quantity > 0) {
          stockText.textContent = 'Only ' + variant.inventory_quantity + ' left';
        } else {
          stockText.textContent = 'In stock';
        }
      }

      if (stickyVariant) stickyVariant.textContent = variant.title === 'Default Title' ? '' : variant.title;
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, data.moneyFormat);

      if (orderTotal) orderTotal.textContent = formatMoney(variant.price * qty, data.moneyFormat);

      updateGalleryForVariant(variant);
      updateTierDescriptions();
      updatePackCards();
    }

    function updateTierDescriptions() {
      groups.forEach(function (group) {
        if (!group.classList.contains('pms__tier-grid')) return;
        var optionIndex = group.getAttribute('data-option-index');
        group.querySelectorAll('.pms__tier-card').forEach(function (card) {
          var candidateVariant = findVariant(objectWith(optionIndex, card.getAttribute('data-value')));
          var descEl = card.querySelector('[data-role="tier-desc"]');
          if (descEl) {
            if (candidateVariant && candidateVariant.tier_description) {
              descEl.textContent = candidateVariant.tier_description;
              descEl.hidden = false;
            } else {
              descEl.hidden = true;
            }
          }
        });
      });
    }

    function updatePackCards() {
      groups.forEach(function (group) {
        if (!group.classList.contains('pms__pack-grid')) return;
        var optionIndex = group.getAttribute('data-option-index');
        var cards = group.querySelectorAll('.pms__pack-card');
        var unitPrices = [];

        cards.forEach(function (card) {
          var candidateVariant = findVariant(objectWith(optionIndex, card.getAttribute('data-value')));
          var perUnit = unitPrice(candidateVariant);
          unitPrices.push(perUnit);
        });

        var baseline = unitPrices.filter(function (p) { return p; }).sort(function (a, b) { return b - a; })[0];

        cards.forEach(function (card, i) {
          var priceEl = card.querySelector('[data-role="pack-price"]');
          var saveEl = card.querySelector('[data-role="pack-save"]');
          var perUnit = unitPrices[i];

          if (priceEl) {
            priceEl.textContent = perUnit ? formatMoney(Math.round(perUnit), data.moneyFormat) + ' / piece' : '';
          }

          if (saveEl) {
            if (perUnit && baseline && perUnit < baseline) {
              var savePct = Math.round((1 - perUnit / baseline) * 100);
              saveEl.textContent = 'Save ' + savePct + '%';
              saveEl.hidden = false;
            } else {
              saveEl.hidden = true;
            }
          }
        });
      });
    }

    function objectWith(key, value) {
      var obj = {};
      obj[key] = value;
      return obj;
    }

    groups.forEach(function (group) {
      var optionIndex = group.getAttribute('data-option-index');
      group.querySelectorAll('button[data-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          selected[optionIndex] = btn.getAttribute('data-value');

          group.querySelectorAll('button[data-value]').forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
          });

          var selectedLabel = root.querySelector('[data-role="option-selected-' + optionIndex + '"]');
          if (selectedLabel) selectedLabel.textContent = selected[optionIndex];

          applyVariant(findVariant());
        });
      });
    });

    // Manual thumbnail browsing. Reads the thumbnail's own <img> src
    // directly (bumped to a larger width) rather than looking the media id
    // up in the JSON blob, so a click always shows something — matches the
    // same fix applied to product-main-catalog.js.
    root.querySelectorAll('[data-role="thumb"]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var thumbImg = thumb.querySelector('img');
        if (!thumbImg) return;

        var src = thumbImg.currentSrc || thumbImg.src;
        src = /[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, '$1width=900') : src;
        setImageSrc(root.querySelector('[data-role="main-media"] img'), src);

        root.querySelectorAll('[data-role="thumb"]').forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
        });
      });
    });

    // Qty stepper
    var qtyDisplay = root.querySelector('[data-role="qty-display"]');
    var qtyInput = root.querySelector('[data-role="qty-input"]');
    var orderTotalEl = root.querySelector('[data-role="order-total"]');

    function setQty(newQty) {
      qty = Math.max(1, Math.min(999, newQty));
      if (qtyDisplay) qtyDisplay.textContent = qty;
      if (qtyInput) qtyInput.value = qty;
      var variant = findVariant();
      if (orderTotalEl && variant) orderTotalEl.textContent = formatMoney(variant.price * qty, data.moneyFormat);
    }

    var decreaseBtn = root.querySelector('[data-role="qty-decrease"]');
    var increaseBtn = root.querySelector('[data-role="qty-increase"]');
    if (decreaseBtn) decreaseBtn.addEventListener('click', function () { setQty(qty - 1); });
    if (increaseBtn) increaseBtn.addEventListener('click', function () { setQty(qty + 1); });

    // Share
    var shareBtn = root.querySelector('[data-role="share"]');
    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        if (navigator.share) {
          navigator.share({ title: document.title, url: window.location.href }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(window.location.href).catch(function () {});
        }
      });
    }

    // Sticky bar show/hide
    var stickyBar = root.querySelector('[data-pms-sticky]');
    var priceBox = root.querySelector('.pms__price-box');
    if (stickyBar && priceBox) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            stickyBar.classList.toggle('show', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        },
        { threshold: 0 }
      );
      observer.observe(priceBox);
    }

    // Add to cart (both the form and the sticky button)
    function addToCart(button) {
      var variantIdInput = root.querySelector('[data-role="variant-id-input"]');
      if (!variantIdInput || !variantIdInput.value) return;

      var originalHTML = button ? button.innerHTML : '';
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding…';
      }

      var merchandiseId = variantIdInput.value;
      var requestedQty = qty;

      // Every cart-items-component on the page (drawer, and the full /cart
      // page if ever rendered alongside) needs its own section id requested
      // so it has fresh markup to morph into once the event below resolves.
      var sectionIds = [];
      document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
        if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });

      // Dispatching this *before* the fetch resolves (promise unresolved)
      // is deliberate and matches Horizon's own <product-form>: it's what
      // lets cart-drawer-component auto-open immediately rather than
      // waiting on the network round trip.
      var deferredEventPromise = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: merchandiseId, quantity: requestedQty }],
          promise: deferredEventPromise.promise,
        })
      );

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: merchandiseId,
          quantity: requestedQty,
          sections: sectionIds.join(','),
        }),
      })
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok) throw new Error(body.description || body.message || 'Could not add to cart');
            return body;
          });
        })
        .then(function (addResponse) {
          if (button) button.textContent = 'Added ✓';

          // The add response only describes the line that was added, not
          // the full cart — a plain GET gives cart-items-component's morph
          // the authoritative item_count/items it needs.
          return fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (cartResponse) {
              return cartResponse.json();
            })
            .then(function (ajaxCart) {
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  source: 'product-main-shop',
                  sourceId: 'product-main-shop',
                  itemCount: requestedQty,
                  sections: addResponse.sections,
                  didError: false,
                },
              });
            });
        })
        .then(function () {
          window.setTimeout(function () {
            if (button) {
              button.disabled = false;
              button.innerHTML = originalHTML;
            }
          }, 1800);
        })
        .catch(function (error) {
          deferredEventPromise.reject(error);
          if (button) {
            button.disabled = false;
            button.textContent = 'Try again';
            window.setTimeout(function () {
              button.innerHTML = originalHTML;
            }, 1800);
          }
          console.error('[fixplus] add to cart failed:', error.message);
        });
    }

    var form = root.querySelector('[data-role="add-form"]');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        addToCart(form.querySelector('[data-role="add-to-cart"]'));
      });
    }

    var stickyAddBtn = root.querySelector('[data-role="sticky-add-to-cart"]');
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', function () {
        addToCart(stickyAddBtn);
      });
    }

    // Initial render pass (per-unit price, tier descriptions, pack cards)
    applyVariant(findVariant());
  }

  // "Click to zoom" — same implementation as product-main-catalog.js: opens
  // whatever image is currently showing in a full-screen overlay at a
  // larger width, with Prev/Next stepping through the currently-visible
  // thumbnails (delegating to each thumbnail's own click handler so the
  // underlying gallery stays in sync with whatever's viewed in the zoom).
  function initZoom(root) {
    var overlay = root.querySelector('[data-role="zoom-overlay"]');
    var zoomImg = overlay ? overlay.querySelector('[data-role="zoom-image"]') : null;
    var closeBtn = overlay ? overlay.querySelector('[data-role="zoom-close"]') : null;
    var prevBtn = overlay ? overlay.querySelector('[data-role="zoom-prev"]') : null;
    var nextBtn = overlay ? overlay.querySelector('[data-role="zoom-next"]') : null;
    if (!overlay || !zoomImg) return;

    var currentIndex = 0;

    function hiResUrl(url) {
      if (!url) return url;
      if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=1600');
      var sep = url.indexOf('?') > -1 ? '&' : '?';
      return url + sep + 'width=1600';
    }

    function visibleThumbs() {
      return Array.prototype.filter.call(root.querySelectorAll('[data-role="thumb"]'), function (t) {
        return !t.hidden;
      });
    }

    function syncArrows() {
      var hide = visibleThumbs().length <= 1;
      if (prevBtn) prevBtn.hidden = hide;
      if (nextBtn) nextBtn.hidden = hide;
    }

    function open() {
      var thumbs = visibleThumbs();
      var mainImg = root.querySelector('[data-role="main-media"] img');
      if (!mainImg) return;

      currentIndex = 0;
      thumbs.forEach(function (t, i) {
        if (t.classList.contains('is-active')) currentIndex = i;
      });

      zoomImg.src = hiResUrl(mainImg.currentSrc || mainImg.src);
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      syncArrows();
    }

    function close() {
      overlay.hidden = true;
      document.body.style.overflow = '';
    }

    function go(direction) {
      var thumbs = visibleThumbs();
      if (thumbs.length === 0) return;
      currentIndex = ((currentIndex + direction) % thumbs.length + thumbs.length) % thumbs.length;
      var thumb = thumbs[currentIndex];
      thumb.click();
      var thumbImg = thumb.querySelector('img');
      if (thumbImg) zoomImg.src = hiResUrl(thumbImg.currentSrc || thumbImg.src);
    }

    root.querySelectorAll('[data-role="zoom-trigger"]').forEach(function (btn) {
      btn.addEventListener('click', open);
    });

    var mainImgEl = root.querySelector('[data-role="main-media"] img');
    if (mainImgEl) mainImgEl.addEventListener('click', open);

    if (closeBtn) closeBtn.addEventListener('click', close);
    zoomImg.addEventListener('click', close);
    if (prevBtn) {
      prevBtn.addEventListener('click', function (event) {
        event.stopPropagation();
        go(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (event) {
        event.stopPropagation();
        go(1);
      });
    }
    document.addEventListener('keydown', function (event) {
      if (overlay.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') go(-1);
      if (event.key === 'ArrowRight') go(1);
    });
  }

  document.querySelectorAll('.pms').forEach(function (root) {
    initProductMain(root);
    initZoom(root);
  });
})();
